/**
 * YNS Study Mode
 * Split-view controller for video + PDF side-by-side study.
 *
 * Features:
 * - Split-view container with resizable panes (desktop)
 * - Vimeo PIP support
 * - Session persistence (video timestamp + PDF page)
 * - Mobile stacked layout with toggle
 * - Personal timestamped notes
 */

(function() {
  'use strict';

  // ============================================
  // Configuration
  // ============================================
  const CONFIG = {
    sessionKey: 'yns_study_sessions',
    notesKey: 'yns_study_notes',
    annotationsKey: 'yns_pdf_annotations', // Shared with PDF viewer
    minPaneWidth: 300,
    defaultSplitRatio: 0.5
  };

  // ============================================
  // State
  // ============================================
  let currentSession = null;
  let selectedVideoId = null;
  let selectedPdfId = null;
  let returnTo = null; // set when Study mode was opened from a lecture page; "Close" goes back there
  let vimeoPlayer = null;
  let pdfDoc = null;
  let currentPdfPage = 1;
  let totalPdfPages = 0;
  let splitRatio = CONFIG.defaultSplitRatio;
  let isResizing = false;
  let isMobileView = false;
  let currentMobilePane = 'video'; // 'video' or 'pdf'
  let notes = [];
  let isFullscreen = false;
  let fullscreenTarget = null; // 'split', 'video', or 'pdf'
  let isCssFullscreen = false; // For iOS Safari fallback

  // Annotation state
  let currentTool = null; // 'highlight' | 'note' | 'bookmark'
  let currentHighlightColor = '#FFEB3B';
  let studyAnnotations = {}; // keyed by pdfId
  let isDrawingHighlight = false;
  let highlightPoints = []; // Array of {x, y} percentage points for freeform drawing

  // Continuous scroll state
  let renderedPages = new Set();     // Track which pages are rendered
  let renderingPages = new Set();    // Track pages currently being rendered
  let scrollDebounceTimer = null;
  const RENDER_BUFFER = 3;           // Pages to render above/below viewport (increased for smoother scrolling)

  // Video data reference
  let VIDEOS = [];

  // ============================================
  // Supabase Data Loading
  // ============================================
  async function loadSessionsFromSupabase() {
    if (!window.YNS_SUPABASE || !window.YNS_CONFIG?.customerId) return;

    try {
      const rows = await window.YNS_SUPABASE.select(window.YNS_SUPABASE.tables.studySessions, {}, {
        orderBy: 'created_at',
        orderDesc: true,
        limit: 1
      });

      if (rows && rows.length > 0) {
        const serverSession = {
          videoId: rows[0].video_id,
          videoTime: rows[0].video_time,
          pdfId: rows[0].pdf_id,
          pdfPage: rows[0].pdf_page,
          timestamp: rows[0].created_at
        };

        // Use server session if it's more recent than local
        if (!currentSession ||
            new Date(serverSession.timestamp) > new Date(currentSession.timestamp)) {
          currentSession = serverSession;
          checkForResumableSession();
        }
      }
    } catch (e) {
      console.error('[Study] Error loading sessions from Supabase:', e);
    }
  }

  async function loadNotesFromSupabase() {
    if (!window.YNS_SUPABASE || !window.YNS_CONFIG?.customerId) return;

    try {
      const rows = await window.YNS_SUPABASE.select(window.YNS_SUPABASE.tables.studyNotes);

      if (rows && rows.length > 0) {
        // Transform server data to local format
        const serverNotes = rows.map(row => ({
          id: row.id,
          videoId: row.video_id,
          videoTime: row.video_time,
          pdfId: row.pdf_id,
          pdfPage: row.pdf_page,
          content: row.content,
          timestamp: row.created_at
        }));

        // Merge with local notes
        notes = window.YNS_SUPABASE.mergeArraysById(notes, serverNotes);
        localStorage.setItem(CONFIG.notesKey, JSON.stringify(notes));
        renderNotes();
      }
    } catch (e) {
      console.error('[Study] Error loading notes from Supabase:', e);
    }
  }

  async function loadStudyAnnotationsFromSupabase(pdfId) {
    if (!window.YNS_SUPABASE || !window.YNS_CONFIG?.customerId) return;

    try {
      const rows = await window.YNS_SUPABASE.select(window.YNS_SUPABASE.tables.pdfAnnotations, {
        pdf_id: pdfId
      });

      if (rows && rows.length > 0) {
        // Transform server data to local format
        const serverAnnotations = rows.map(row => ({
          id: row.id,
          type: row.annotation_type,
          page: row.page_number,
          points: row.points,
          strokeWidth: row.stroke_width,
          color: row.color,
          x: row.x_position,
          y: row.y_position,
          content: row.content,
          createdAt: row.created_at
        }));

        // Merge with local annotations
        const localAnnotations = studyAnnotations[pdfId] || [];
        studyAnnotations[pdfId] = window.YNS_SUPABASE.mergeArraysById(localAnnotations, serverAnnotations);
        localStorage.setItem(CONFIG.annotationsKey, JSON.stringify(studyAnnotations));
        refreshAnnotations();
      }
    } catch (e) {
      console.error('[Study] Error loading annotations from Supabase:', e);
    }
  }

  // ============================================
  // Initialization
  // ============================================
  function init() {
    // Check for mobile view
    isMobileView = window.innerWidth <= 768;

    // Load saved sessions, notes, and annotations
    loadSessions();
    loadNotes();
    loadStudyAnnotations();

    // Load from Supabase in background (merge with local)
    setTimeout(() => {
      loadSessionsFromSupabase();
      loadNotesFromSupabase();
    }, 500);

    // Get videos from existing video library data
    loadVideosFromDOM();

    // Setup UI
    populateVideoSelector();
    populatePdfSelector();
    setupEventListeners();
    checkForResumableSession();

    // Handle window resize
    window.addEventListener('resize', handleResize);
  }

  // Load videos from script or create minimal set
  // Lectures come from YNS_SECURE_DATA (vimeoId only present when the customer has access),
  // plus the free 45-minute crash course lecture on YouTube (marked with a "yt:" id).

  function loadVideosFromDOM() {
    const secure = (window.YNS_SECURE_DATA && window.YNS_SECURE_DATA.videos) || [];
    VIDEOS = secure.map(v => ({ id: v.id, vimeoId: v.vimeoId || null, vimeoHash: v.vimeoHash || null, title: v.title, category: v.category, hasAccess: !!v.hasAccess, purchaseUrl: v.purchaseUrl || null }));
    // free sample lectures (YouTube) for series they don't own yet
    const loggedIn = !document.getElementById('ynsEducationHub')?.classList.contains('is-guest');
    const samples = loggedIn && window.YNSSampleLectures ? window.YNSSampleLectures() : [];
    samples.forEach(sv => {
      if (VIDEOS.some(v => v.category === sv.category && v.hasAccess && v.vimeoId)) return;
      VIDEOS.push({ id: sv.id, vimeoId: sv.vimeoId, vimeoHash: null, title: sv.title, category: sv.category, hasAccess: true, sample: true, purchaseUrl: null });
    });

  }

  // ============================================
  // Session Management
  // ============================================
  function loadSessions() {
    try {
      const stored = localStorage.getItem(CONFIG.sessionKey);
      const sessions = stored ? JSON.parse(stored) : [];
      // Get most recent session
      if (sessions.length > 0) {
        currentSession = sessions[sessions.length - 1];
      }
    } catch (e) {
      currentSession = null;
    }
  }

  function saveSession() {
    if (!selectedVideoId || !selectedPdfId) return;

    const session = {
      videoId: selectedVideoId,
      videoTime: vimeoPlayer ? getCurrentVideoTime() : 0,
      pdfId: selectedPdfId,
      pdfPage: currentPdfPage,
      timestamp: new Date().toISOString()
    };

    try {
      const sessions = JSON.parse(localStorage.getItem(CONFIG.sessionKey) || '[]');
      // Keep only last 10 sessions
      const updatedSessions = [...sessions.slice(-9), session];
      localStorage.setItem(CONFIG.sessionKey, JSON.stringify(updatedSessions));
      currentSession = session;

      // Sync to Supabase
      if (window.YNS_SUPABASE && window.YNS_CONFIG?.customerId) {
        window.YNS_SUPABASE.queueSync('study_session', async () => {
          await window.YNS_SUPABASE.insert(window.YNS_SUPABASE.tables.studySessions, {
            video_id: session.videoId,
            video_time: session.videoTime,
            pdf_id: session.pdfId,
            pdf_page: session.pdfPage,
            created_at: session.timestamp
          });
        });
      }
    } catch (e) {
      console.error('Error saving session:', e);
    }
  }

  function getCurrentVideoTime() {
    // Will be updated by Vimeo player events
    return currentSession?.videoTime || 0;
  }

  function checkForResumableSession() {
    if (!currentSession) return;

    const resumeCard = document.getElementById('sessionResumeCard');
    const description = document.getElementById('sessionResumeDescription');

    if (!resumeCard) return;

    // Find video and PDF info
    const video = VIDEOS.find(v => v.id === currentSession.videoId);
    const pdfData = window.YNS_PDF_DATA;
    const pdf = getPdf(currentSession.pdfId);

    if (video && pdf) {
      const videoTime = formatTime(currentSession.videoTime);
      description.textContent = `${video.title} at ${videoTime} + ${pdf.title} page ${currentSession.pdfPage}`;
      resumeCard.style.display = 'flex';
    }
  }

  function resumeSession() {
    if (!currentSession) return;

    selectedVideoId = currentSession.videoId;
    selectedPdfId = currentSession.pdfId;

    // Update UI
    updateSelectedVideo();
    updateSelectedPdf();

    // Start the session
    startStudySession(currentSession.videoTime, currentSession.pdfPage);
  }

  // ============================================
  // Notes Management
  // ============================================
  function loadNotes() {
    try {
      const stored = localStorage.getItem(CONFIG.notesKey);
      notes = stored ? JSON.parse(stored) : [];
    } catch (e) {
      notes = [];
    }
  }

  function saveNotes() {
    try {
      localStorage.setItem(CONFIG.notesKey, JSON.stringify(notes));

      // Sync notes to Supabase
      if (window.YNS_SUPABASE && window.YNS_CONFIG?.customerId) {
        notes.forEach(note => {
          window.YNS_SUPABASE.queueSync(`note:${note.id}`, async () => {
            await window.YNS_SUPABASE.upsert(window.YNS_SUPABASE.tables.studyNotes, {
              id: note.id,
              video_id: note.videoId || null,
              video_time: note.videoTime || null,
              pdf_id: note.pdfId || null,
              pdf_page: note.pdfPage || null,
              content: note.content,
              created_at: note.timestamp
            }, ['id']);
          });
        });
      }
    } catch (e) {
      console.error('Error saving notes:', e);
    }
  }

  function addNote(content) {
    const note = {
      id: generateId(),
      videoId: selectedVideoId,
      videoTime: getCurrentVideoTime(),
      pdfId: selectedPdfId,
      pdfPage: currentPdfPage,
      content,
      timestamp: new Date().toISOString()
    };

    notes.push(note);
    saveNotes();
    renderNotes();

    return note;
  }

  function deleteNote(noteId) {
    notes = notes.filter(n => n.id !== noteId);
    saveNotes();

    // Delete from Supabase
    if (window.YNS_SUPABASE && window.YNS_CONFIG?.customerId) {
      window.YNS_SUPABASE.queueSync(`delete_note:${noteId}`, async () => {
        await window.YNS_SUPABASE.delete(window.YNS_SUPABASE.tables.studyNotes, {
          id: noteId
        });
      });
    }

    renderNotes();
  }

  function renderNotes() {
    const notesList = document.getElementById('notesList');
    if (!notesList) return;

    // Filter notes for current session
    const sessionNotes = notes.filter(n =>
      n.videoId === selectedVideoId || n.pdfId === selectedPdfId
    ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    if (sessionNotes.length === 0) {
      notesList.innerHTML = '<p class="yns-no-notes">No notes yet. Type one above and press Enter.</p>';
      return;
    }

    notesList.innerHTML = sessionNotes.map(note => `
      <div class="yns-note-item" data-note-id="${note.id}">
        <div class="yns-note-timestamp">
          ${note.videoTime ? formatTime(note.videoTime) : ''}
          ${note.pdfPage ? `p.${note.pdfPage}` : ''}
        </div>
        <div class="yns-note-content">${escapeHtml(note.content)}</div>
        <button class="yns-note-delete" title="Delete note">×</button>
      </div>
    `).join('');

    // Add delete handlers
    notesList.querySelectorAll('.yns-note-delete').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const noteId = btn.closest('.yns-note-item').dataset.noteId;
        deleteNote(noteId);
      });
    });

    // Add click to jump handlers
    notesList.querySelectorAll('.yns-note-item').forEach(item => {
      item.addEventListener('click', () => {
        const noteId = item.dataset.noteId;
        const note = notes.find(n => n.id === noteId);
        if (note) {
          if (note.videoTime && vimeoPlayer) {
            vimeoPlayer.setCurrentTime(note.videoTime);
          }
          if (note.pdfPage) {
            goToPdfPage(note.pdfPage);
          }
        }
      });
    });
  }

  // ============================================
  // Annotation Management (shared with PDF viewer)
  // ============================================
  function loadStudyAnnotations() {
    try {
      const stored = localStorage.getItem(CONFIG.annotationsKey);
      studyAnnotations = stored ? JSON.parse(stored) : {};
    } catch (e) {
      studyAnnotations = {};
    }
  }

  function saveStudyAnnotations() {
    try {
      localStorage.setItem(CONFIG.annotationsKey, JSON.stringify(studyAnnotations));
      // Dispatch event for PDF viewer to sync
      document.dispatchEvent(new CustomEvent('yns:annotationsUpdated', {
        detail: { pdfId: selectedPdfId },
        bubbles: true
      }));

      // Sync annotations to Supabase
      if (window.YNS_SUPABASE && window.YNS_CONFIG?.customerId && selectedPdfId) {
        const pdfAnnotations = studyAnnotations[selectedPdfId] || [];
        pdfAnnotations.forEach(ann => {
          window.YNS_SUPABASE.queueSync(`annotation:${ann.id}`, async () => {
            await window.YNS_SUPABASE.upsert(window.YNS_SUPABASE.tables.pdfAnnotations, {
              id: ann.id,
              pdf_id: selectedPdfId,
              annotation_type: ann.type,
              page_number: ann.page,
              points: ann.points || null,
              stroke_width: ann.strokeWidth || null,
              color: ann.color || null,
              x_position: ann.x || null,
              y_position: ann.y || null,
              content: ann.content || null,
              created_at: ann.createdAt
            }, ['id']);
          });
        });
      }
    } catch (e) {
      console.error('Error saving annotations:', e);
    }
  }

  function getAnnotationsForCurrentPdf() {
    return studyAnnotations[selectedPdfId] || [];
  }

  function addStudyAnnotation(type, data) {
    return addStudyAnnotationToPage(type, currentPdfPage, data);
  }

  function addStudyAnnotationToPage(type, pageNum, data) {
    if (!selectedPdfId) {
      console.warn('[Study] No selectedPdfId - annotation aborted');
      return null;
    }

    const annotation = {
      id: 'ann_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
      type,
      page: pageNum,
      ...data,
      createdAt: new Date().toISOString()
    };

    if (!studyAnnotations[selectedPdfId]) {
      studyAnnotations[selectedPdfId] = [];
    }
    studyAnnotations[selectedPdfId].push(annotation);
    saveStudyAnnotations();

    // Re-render annotations for the specific page
    const layer = document.querySelector(`.yns-study-annotation-layer[data-page="${pageNum}"]`);
    if (layer) {
      renderPageAnnotations(pageNum, layer);
    }

    return annotation;
  }

  function removeStudyAnnotation(annotationId) {
    if (!selectedPdfId || !studyAnnotations[selectedPdfId]) return;

    // Find the annotation to get its page number before removing
    const annotation = studyAnnotations[selectedPdfId].find(a => a.id === annotationId);
    const pageNum = annotation ? annotation.page : currentPdfPage;

    studyAnnotations[selectedPdfId] = studyAnnotations[selectedPdfId].filter(
      a => a.id !== annotationId
    );
    saveStudyAnnotations();

    // Delete from Supabase
    if (window.YNS_SUPABASE && window.YNS_CONFIG?.customerId) {
      window.YNS_SUPABASE.queueSync(`delete_annotation:${annotationId}`, async () => {
        await window.YNS_SUPABASE.delete(window.YNS_SUPABASE.tables.pdfAnnotations, {
          id: annotationId
        });
      });
    }

    // Re-render the specific page where the annotation was
    const layer = document.querySelector(`.yns-study-annotation-layer[data-page="${pageNum}"]`);
    if (layer) {
      renderPageAnnotations(pageNum, layer);
    }
  }

  function renderStudyAnnotations(pageNum) {
    // For backwards compatibility, delegate to renderPageAnnotations
    const layer = document.querySelector(`.yns-study-annotation-layer[data-page="${pageNum}"]`);
    if (layer) {
      renderPageAnnotations(pageNum, layer);
    }
  }

  function renderPageAnnotations(pageNum, layer) {
    if (!layer) return;

    // Clear existing annotations
    layer.innerHTML = '';

    const annotations = getAnnotationsForCurrentPdf().filter(a => a.page === pageNum);

    annotations.forEach(ann => {
      if (ann.type === 'highlight') {
        // Check if this is a new freeform path highlight or legacy rectangle
        if (ann.points) {
          // New freeform path rendering
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.setAttribute('class', 'yns-study-highlight-svg');
          svg.setAttribute('viewBox', '0 0 100 100');
          svg.setAttribute('preserveAspectRatio', 'none');
          svg.style.position = 'absolute';
          svg.style.left = '0';
          svg.style.top = '0';
          svg.style.width = '100%';
          svg.style.height = '100%';
          svg.style.pointerEvents = 'none';
          svg.style.overflow = 'visible';
          svg.dataset.annotationId = ann.id;

          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', pointsToSmoothPath(ann.points));
          path.setAttribute('stroke', ann.color);
          path.setAttribute('stroke-width', ann.strokeWidth || 15);
          path.setAttribute('stroke-linecap', 'round');
          path.setAttribute('stroke-linejoin', 'round');
          path.setAttribute('fill', 'none');
          path.setAttribute('opacity', '0.4');
          path.style.pointerEvents = 'stroke';
          path.setAttribute('class', 'yns-study-highlight-path');

          svg.appendChild(path);

          // Create a wrapper div for the delete button positioning
          const wrapper = document.createElement('div');
          wrapper.className = 'yns-study-highlight-wrapper';
          wrapper.dataset.annotationId = ann.id;

          // Position the delete button at the start of the path
          const firstPoint = ann.points[0];
          wrapper.style.position = 'absolute';
          wrapper.style.left = firstPoint.x + '%';
          wrapper.style.top = firstPoint.y + '%';

          // Delete button
          const deleteBtn = document.createElement('button');
          deleteBtn.className = 'yns-study-annotation-delete';
          deleteBtn.innerHTML = '×';
          deleteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            removeStudyAnnotation(ann.id);
          });
          wrapper.appendChild(deleteBtn);

          // Add click handler to path for showing delete button
          path.addEventListener('click', (e) => {
            e.stopPropagation();
            // Toggle delete button visibility
            wrapper.classList.toggle('show-delete');
          });

          layer.appendChild(svg);
          layer.appendChild(wrapper);
        } else if (ann.width !== undefined) {
          // Legacy rectangle rendering (for existing annotations)
          const el = document.createElement('div');
          el.className = 'yns-study-highlight';
          el.style.left = ann.x + '%';
          el.style.top = ann.y + '%';
          el.style.width = ann.width + '%';
          el.style.height = ann.height + '%';
          el.style.backgroundColor = ann.color;
          el.dataset.annotationId = ann.id;

          // Delete button
          const deleteBtn = document.createElement('button');
          deleteBtn.className = 'yns-study-annotation-delete';
          deleteBtn.innerHTML = '×';
          deleteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            removeStudyAnnotation(ann.id);
          });
          el.appendChild(deleteBtn);

          layer.appendChild(el);
        }
      } else if (ann.type === 'note') {
        const el = document.createElement('div');
        el.className = 'yns-study-note';
        el.style.left = ann.x + '%';
        el.style.top = ann.y + '%';
        el.dataset.annotationId = ann.id;

        // Content div (visible text)
        const content = document.createElement('div');
        content.className = 'yns-study-note-content';
        content.textContent = ann.content;
        el.appendChild(content);

        // Delete button
        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'yns-study-annotation-delete';
        deleteBtn.innerHTML = '×';
        deleteBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          removeStudyAnnotation(ann.id);
        });
        el.appendChild(deleteBtn);

        // Click to edit
        el.addEventListener('click', (e) => {
          if (e.target === deleteBtn) return;
          startEditingNote(el, ann, pageNum, layer);
        });

        // Make draggable
        setupNoteDrag(el, ann, layer);

        layer.appendChild(el);
      } else if (ann.type === 'bookmark') {
        const el = document.createElement('div');
        el.className = 'yns-study-bookmark';
        el.dataset.annotationId = ann.id;
        el.title = 'Bookmarked page';
        el.addEventListener('click', () => {
          removeStudyAnnotation(ann.id);
          updateBookmarkButton();
        });
        layer.appendChild(el);
      }
    });

    updateBookmarkButton();
  }

  // Re-render annotations on all rendered pages when they change
  function refreshAnnotations() {
    renderedPages.forEach(pageNum => {
      const layer = document.querySelector(`.yns-study-annotation-layer[data-page="${pageNum}"]`);
      if (layer) renderPageAnnotations(pageNum, layer);
    });
  }

  // ============================================
  // Annotation Tool Management
  // ============================================
  function setStudyTool(tool) {
    const tools = document.querySelectorAll('.yns-study-tool');
    const colorPicker = document.getElementById('studyColorPicker');
    const container = document.getElementById('studyPdfScrollContainer');

    // Toggle same tool off
    if (currentTool === tool) {
      currentTool = null;
      tools.forEach(t => t.classList.remove('active'));
      colorPicker?.classList.remove('visible');
      container?.classList.remove('tool-active');
      // Remove tool-active from individual layers
      document.querySelectorAll('.yns-study-annotation-layer').forEach(layer => {
        layer.classList.remove('tool-active');
      });
      return;
    }

    currentTool = tool;

    // Update tool button states
    tools.forEach(t => {
      const toolType = t.dataset.tool;
      if (toolType === 'bookmark') {
        // Bookmark doesn't get active state, handled separately
        return;
      }
      t.classList.toggle('active', toolType === tool);
    });

    // Show/hide color picker for highlight tool
    if (tool === 'highlight') {
      colorPicker?.classList.add('visible');
    } else {
      colorPicker?.classList.remove('visible');
    }

    // Enable/disable annotation layer interaction on scroll container
    if (tool === 'highlight' || tool === 'note') {
      container?.classList.add('tool-active');

      const layers = document.querySelectorAll('.yns-study-annotation-layer');

      layers.forEach((layer) => {
        layer.classList.add('tool-active');
      });
    } else {
      container?.classList.remove('tool-active');
      document.querySelectorAll('.yns-study-annotation-layer').forEach(layer => {
        layer.classList.remove('tool-active');
      });
    }

    // Handle bookmark immediately
    if (tool === 'bookmark') {
      toggleStudyBookmark();
      currentTool = null;
      tools.forEach(t => t.classList.remove('active'));
    }
  }

  function setStudyColor(color) {
    currentHighlightColor = color;

    // Update color button states
    document.querySelectorAll('.yns-study-color-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.color === color);
    });
  }

  function toggleStudyBookmark() {
    if (!selectedPdfId) {
      console.warn('[Study] No selectedPdfId - bookmark aborted');
      return;
    }

    const annotations = getAnnotationsForCurrentPdf();
    const existingBookmark = annotations.find(
      a => a.type === 'bookmark' && a.page === currentPdfPage
    );

    if (existingBookmark) {
      removeStudyAnnotation(existingBookmark.id);
      showToast('Bookmark Removed', `Page ${currentPdfPage} unbookmarked`);
    } else {
      addStudyAnnotation('bookmark', {});
      showToast('Bookmark Added', `Page ${currentPdfPage} bookmarked`);
    }

    updateBookmarkButton();
  }

  function updateBookmarkButton() {
    const bookmarkBtn = document.getElementById('studyBookmarkBtn');
    if (!bookmarkBtn) return;

    const annotations = getAnnotationsForCurrentPdf();
    const isBookmarked = annotations.some(
      a => a.type === 'bookmark' && a.page === currentPdfPage
    );

    bookmarkBtn.classList.toggle('bookmarked', isBookmarked);
  }

  // ============================================
  // Annotation Canvas Interaction
  // ============================================
  // Track the layer being interacted with for highlight drawing
  let activeDrawingLayer = null;

  function handleAnnotationLayerClick(e) {
    if (!currentTool || currentTool === 'bookmark') {
      return;
    }

    const layer = e.target.closest('.yns-study-annotation-layer');
    if (!layer) {
      return;
    }

    const pageNum = parseInt(layer.dataset.page, 10);
    const rect = layer.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (currentTool === 'note') {
      // Create inline textarea at click position
      const textarea = document.createElement('textarea');
      textarea.className = 'yns-study-note-input';
      textarea.style.left = x + '%';
      textarea.style.top = y + '%';
      textarea.placeholder = 'Type your note...';
      layer.appendChild(textarea);
      textarea.focus();

      let saved = false;

      // Save on blur or Enter
      const saveNote = () => {
        if (saved) return;
        saved = true;
        const content = textarea.value.trim();
        if (content) {
          addStudyAnnotationToPage('note', pageNum, { x, y, content });
          showToast('Note Added', 'Your note has been saved');
        }
        textarea.remove();
      };

      textarea.addEventListener('blur', saveNote);
      textarea.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          saveNote();
        }
        if (e.key === 'Escape') {
          saved = true; // Prevent saving on blur
          textarea.remove();
        }
      });
    }
  }

  function handleAnnotationLayerMouseDown(e) {
    if (currentTool !== 'highlight') {
      return;
    }

    const layer = e.target.closest('.yns-study-annotation-layer');
    if (!layer) {
      return;
    }

    activeDrawingLayer = layer;

    const rect = layer.getBoundingClientRect();
    const startX = ((e.clientX - rect.left) / rect.width) * 100;
    const startY = ((e.clientY - rect.top) / rect.height) * 100;

    // Initialize points array with first point
    highlightPoints = [{ x: startX, y: startY }];
    isDrawingHighlight = true;

    // Create SVG preview element for freeform drawing
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.id = 'highlightPreview';
    svg.setAttribute('class', 'yns-study-highlight-svg');
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.style.position = 'absolute';
    svg.style.left = '0';
    svg.style.top = '0';
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.pointerEvents = 'none';
    svg.style.overflow = 'visible';

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', `M ${startX} ${startY}`);
    path.setAttribute('stroke', currentHighlightColor);
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('fill', 'none');
    path.setAttribute('opacity', '0.5');
    path.id = 'highlightPreviewPath';

    svg.appendChild(path);
    layer.appendChild(svg);
  }

  function handleAnnotationLayerMouseMove(e) {
    if (!isDrawingHighlight || highlightPoints.length === 0 || !activeDrawingLayer) return;

    const path = document.getElementById('highlightPreviewPath');
    if (!path) return;

    const rect = activeDrawingLayer.getBoundingClientRect();
    const currentX = ((e.clientX - rect.left) / rect.width) * 100;
    const currentY = ((e.clientY - rect.top) / rect.height) * 100;

    // Only add point if moved enough (>0.5% of page) to avoid too many points
    const lastPoint = highlightPoints[highlightPoints.length - 1];
    const distance = Math.sqrt(Math.pow(currentX - lastPoint.x, 2) + Math.pow(currentY - lastPoint.y, 2));

    if (distance > 0.5) {
      highlightPoints.push({ x: currentX, y: currentY });
      // Update the SVG path with smooth curves
      path.setAttribute('d', pointsToSmoothPath(highlightPoints));
    }
  }

  function handleAnnotationLayerMouseUp(e) {
    if (!isDrawingHighlight || highlightPoints.length === 0 || !activeDrawingLayer) return;

    const preview = document.getElementById('highlightPreview');
    if (preview) preview.remove();

    const pageNum = parseInt(activeDrawingLayer.dataset.page, 10);

    // Only create highlight if we have enough points (meaningful stroke)
    if (highlightPoints.length >= 2) {
      addStudyAnnotationToPage('highlight', pageNum, {
        points: highlightPoints,
        strokeWidth: 1.5,
        color: currentHighlightColor
      });
      showToast('Highlight Added', 'Your highlight has been saved');
    }

    isDrawingHighlight = false;
    highlightPoints = [];
    activeDrawingLayer = null;
  }

  // Convert points array to smooth SVG path using quadratic bezier curves
  function pointsToSmoothPath(points) {
    if (!points || points.length === 0) return '';
    if (points.length === 1) {
      return `M ${points[0].x} ${points[0].y} L ${points[0].x} ${points[0].y}`;
    }

    let d = `M ${points[0].x} ${points[0].y}`;

    for (let i = 1; i < points.length - 1; i++) {
      const midX = (points[i].x + points[i + 1].x) / 2;
      const midY = (points[i].y + points[i + 1].y) / 2;
      d += ` Q ${points[i].x} ${points[i].y} ${midX} ${midY}`;
    }

    // Last point
    const last = points[points.length - 1];
    d += ` L ${last.x} ${last.y}`;

    return d;
  }

  // ============================================
  // Note Editing & Dragging
  // ============================================

  // Convert a note to edit mode
  function startEditingNote(el, ann, pageNum, layer) {
    // Don't edit if already editing
    if (el.classList.contains('editing')) return;
    el.classList.add('editing');

    const content = el.querySelector('.yns-study-note-content');
    const originalText = content.textContent;

    // Replace content with textarea
    const textarea = document.createElement('textarea');
    textarea.className = 'yns-study-note-textarea';
    textarea.value = originalText;
    content.style.display = 'none';
    el.insertBefore(textarea, content);
    textarea.focus();
    textarea.select();

    const saveEdit = () => {
      const newText = textarea.value.trim();
      if (newText && newText !== originalText) {
        updateAnnotationContent(ann.id, newText);
        content.textContent = newText;
      }
      textarea.remove();
      content.style.display = '';
      el.classList.remove('editing');
    };

    textarea.addEventListener('blur', saveEdit);
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        saveEdit();
      }
      if (e.key === 'Escape') {
        textarea.remove();
        content.style.display = '';
        el.classList.remove('editing');
      }
    });
  }

  // Make notes draggable
  function setupNoteDrag(el, ann, layer) {
    let isDragging = false;
    let hasMoved = false;
    let dragStartX, dragStartY, initialX, initialY;

    el.addEventListener('mousedown', (e) => {
      if (e.target.classList.contains('yns-study-annotation-delete')) return;
      if (el.classList.contains('editing')) return;

      isDragging = true;
      hasMoved = false;
      el.classList.add('dragging');
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      initialX = ann.x;
      initialY = ann.y;
      e.preventDefault();
    });

    const handleDrag = (e) => {
      if (!isDragging) return;
      const rect = layer.getBoundingClientRect();
      const deltaX = ((e.clientX - dragStartX) / rect.width) * 100;
      const deltaY = ((e.clientY - dragStartY) / rect.height) * 100;

      if (Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1) hasMoved = true;

      const newX = Math.max(0, Math.min(95, initialX + deltaX));
      const newY = Math.max(0, Math.min(95, initialY + deltaY));
      el.style.left = newX + '%';
      el.style.top = newY + '%';
    };

    const handleDragEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      el.classList.remove('dragging');

      if (hasMoved) {
        const newX = parseFloat(el.style.left);
        const newY = parseFloat(el.style.top);
        updateAnnotationPosition(ann.id, newX, newY);
      }
    };

    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleDragEnd);
  }

  // Update annotation position in storage
  function updateAnnotationPosition(annotationId, newX, newY) {
    if (!selectedPdfId || !studyAnnotations[selectedPdfId]) return;
    const annotation = studyAnnotations[selectedPdfId].find(a => a.id === annotationId);
    if (annotation) {
      annotation.x = newX;
      annotation.y = newY;
      saveStudyAnnotations();
    }
  }

  // Update annotation content in storage
  function updateAnnotationContent(annotationId, newContent) {
    if (!selectedPdfId || !studyAnnotations[selectedPdfId]) return;
    const annotation = studyAnnotations[selectedPdfId].find(a => a.id === annotationId);
    if (annotation) {
      annotation.content = newContent;
      saveStudyAnnotations();
    }
  }

  // Setup event handlers for a single annotation layer
  function setupAnnotationLayerEvents(layer) {
    layer.addEventListener('click', handleAnnotationLayerClick);
    layer.addEventListener('mousedown', handleAnnotationLayerMouseDown);
    layer.addEventListener('mousemove', handleAnnotationLayerMouseMove);
    layer.addEventListener('mouseup', handleAnnotationLayerMouseUp);
    layer.addEventListener('mouseleave', () => {
      // Cancel highlight if mouse leaves layer
      if (isDrawingHighlight && activeDrawingLayer === layer) {
        const preview = document.getElementById('highlightPreview');
        if (preview) preview.remove();
        isDrawingHighlight = false;
        highlightPoints = [];
        activeDrawingLayer = null;
      }
    });

    // Touch support for mobile
    layer.addEventListener('touchstart', (e) => {
      if (currentTool === 'note') {
        const touch = e.touches[0];
        handleAnnotationLayerClick({ clientX: touch.clientX, clientY: touch.clientY, target: layer });
      }
    });
  }

  // ============================================
  // Page Navigation Input
  // ============================================
  function handlePageInput(e) {
    if (e.key === 'Enter') {
      const input = document.getElementById('studyPdfPageInput');
      if (!input) return;

      const pageNum = parseInt(input.value, 10);
      if (pageNum >= 1 && pageNum <= totalPdfPages) {
        goToPdfPage(pageNum);
      } else {
        // Reset to current page if invalid
        input.value = currentPdfPage;
      }
      input.blur();
    }
  }

  function updatePageInput() {
    const input = document.getElementById('studyPdfPageInput');
    const total = document.getElementById('studyPdfTotal');

    if (input) {
      input.value = currentPdfPage;
      input.max = totalPdfPages;
    }
    if (total) {
      total.textContent = totalPdfPages;
    }
  }

  // ============================================
  // Video & PDF Selection
  // ============================================
  // Lecture picker: one tab per lecture series (Pink Book, Gold Book, Mark Klimek, ICU Survival), then that series' lectures.
  // Only lectures the customer can watch are listed. The open tab is remembered while the page is open.
  const SERIES = [
    { key: 'pink', label: 'Pink Book', test: v => v.category === 'pink-book', url: '/products/the-pink-book-nclex-ngn-test-taking-strategy-guide' },
    { key: 'gold', label: 'Gold Book', test: v => v.category === 'gold-book', url: '/products/nclex-core-bundle' },
    { key: 'klimek', label: 'Mark Klimek', test: v => !['pink-book', 'gold-book', 'icu', 'crash-course', 'school'].includes(v.category), url: '/products/marks-2026-nclex-review-mega-bundle-video-lectures-pdf-study-guides-professor-mark-johnsons-nclex-mega-bundle' },
    { key: 'crash', label: 'Crash Course', test: v => v.category === 'crash-course', url: '/products/2026-nclex-crash-course-notes-high-yield-study-guide-test-taking-strategies' },
    { key: 'icu', label: 'ICU Survival', test: v => v.category === 'icu', url: '/products/icu-survival-notes-the-ultimate-critical-care-nursing-guide' },
    { key: 'school', label: 'Nursing School', test: v => v.category === 'school', soon: true }
  ];
  let videoSeriesTab = null;

  function populateVideoSelector() {
    const menu = document.getElementById('videoSelectorMenu');
    if (!menu) return;

    loadVideosFromDOM();
    const playable = VIDEOS.filter(v => v.vimeoId && v.hasAccess);
    const series = SERIES.map(s => ({ ...s, videos: playable.filter(s.test) }));

    const chosen = playable.find(v => v.id === selectedVideoId);
    if (!videoSeriesTab || series.find(s => s.key === videoSeriesTab).soon) {
      const own = chosen && series.find(s => s.test(chosen));
      videoSeriesTab = own ? own.key : (series.find(s => s.videos.length) || series[0]).key;
    }
    const current = series.find(s => s.key === videoSeriesTab);

    let list = '';
    const sampleTitles = VIDEOS.filter(v => v.sample).map(v => v.title.replace(/ \(free sample\)$/, '').toLowerCase());
    const lockedHere = VIDEOS.filter(v => current.test(v) && !(v.vimeoId && v.hasAccess) && !sampleTitles.some(t => String(v.title || '').toLowerCase().includes(t)));
    const lockedHtml = lockedHere.length ? `<div class="yns-selector-group-label">Unlock to watch</div>` + lockedHere.map(v => `<button type="button" class="yns-selector-option is-locked" data-ypeek="${escapeHtml(String(current.url || '').split('/products/')[1] || '')}" data-ypeek-title="${escapeHtml(current.label + ' lectures')}" data-ypeek-kind="course"><span class="yns-selector-lock" aria-hidden="true">&#128274;</span>${escapeHtml(v.title)}</button>`).join('') : '';
    if (!current.videos.length && lockedHere.length) {
      list = lockedHtml;
    } else if (!current.videos.length) {
      list = `<div class="yns-selector-locked"><p>${current.label} lectures are not in your Study Space yet.</p>${current.url ? `<a href="${current.url}">See what's inside</a>` : ''}</div>`;
    } else if (current.key === 'klimek') {
      const byCategory = {};
      current.videos.forEach(v => { (byCategory[v.category] = byCategory[v.category] || []).push(v); });
      Object.entries(byCategory).forEach(([category, vids]) => {
        list += `<div class="yns-selector-group-label">${formatCategory(category)}</div>`;
        vids.forEach(v => { list += `<button type="button" class="yns-selector-option${v.id === selectedVideoId ? ' is-on' : ''}" data-video-id="${v.id}">${escapeHtml(v.title)}</button>`; });
      });
    } else {
      current.videos.forEach((v, i) => {
        list += `<button type="button" class="yns-selector-option${v.id === selectedVideoId ? ' is-on' : ''}" data-video-id="${v.id}"><span class="yns-selector-n">${v.sample ? '&#9654;' : i + 1}</span>${escapeHtml(v.title)}</button>`;
      });
      list += lockedHtml;
    }

    menu.innerHTML = `<div class="yns-series-tabs" role="tablist" aria-label="Lecture series">${series.map(s =>
      s.soon
        ? `<button type="button" class="yns-series-tab is-soon" disabled title="Coming soon">${s.label}<small>Soon</small></button>`
        : `<button type="button" role="tab" class="yns-series-tab${s.key === current.key ? ' is-on' : ''}${s.videos.length ? '' : ' is-locked'}" aria-selected="${s.key === current.key}" data-series="${s.key}">${s.label}<small>${s.videos.length || '&#128274;'}</small></button>`).join('')}</div>
      <div class="yns-series-list">${list}</div>`;

    menu.querySelectorAll('.yns-series-tab:not(.is-soon)').forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.stopPropagation();
        videoSeriesTab = tab.dataset.series;
        populateVideoSelector();
        menu.classList.add('active');
      });
    });
    menu.querySelectorAll('.yns-selector-option:not(.is-locked)').forEach(option => {
      option.addEventListener('click', () => {
        selectedVideoId = isNaN(+option.dataset.videoId) ? option.dataset.videoId : parseInt(option.dataset.videoId);
        updateSelectedVideo();
        suggestBookFor(VIDEOS.find(v => v.id === selectedVideoId));
        closeDropdown('videoSelectorMenu');
        checkStartEnabled();
        populateVideoSelector();
      });
    });
  }

  // Books currently sold, matched by id or title. Old products (e.g. the Ultimate Mastery "How To", Must Review and Ultimate Review files) are left out.
  const CURRENT_BOOKS = [
    { key: 'pink', name: 'The Pink Book', ids: ['core-pink-book'], re: /pink book/i },
    { key: 'gold', name: 'The Gold Book', ids: ['core-gold-book'], re: /gold book/i },
    { key: 'mastery', name: 'Ultimate Mastery Book', ids: ['nclex-mastery-notes'], re: /^(2026 )?nclex ultimate mastery/i },
    { key: 'crash', name: '45-Minute Crash Course Book', ids: ['nclex-crash-course'], re: /crash course/i },
    { key: 'pharm', name: 'Pharmacology Mastery Book', ids: ['pharmacology-notes'], re: /pharmacology mastery/i },
    { key: 'icu', name: 'The ICU Survival Book', ids: ['icu-survival-book'], re: /icu survival/i },
    { key: 'ecg', name: 'ECG & Dysrhythmia Interpretation', ids: ['ecg-bundle'], re: /ecg/i },
    { key: 'cc', name: 'Critical Care Meds Flashcards', ids: ['critical-care-flashcards'], re: /critical care/i },
    { key: 'blue', name: 'Blue Book (Mark Klimek quiz)', ids: ['mk-blue-book'], re: /^blue book/i },
    { key: 'yellow', name: 'Yellow Book (Mark Klimek lecture notes)', ids: ['mk-yellow-book'], re: /^yellow book/i }
  ];
  function bookFor(pdf) {
    const sec = ((window.YNS_SECURE_DATA && window.YNS_SECURE_DATA.pdfs) || []).find(p => p.id === pdf.id);
    const lid = pdf.legacyId || (sec && sec.legacyId) || pdf.id || '';
    if (/^qbank-pdf/.test(lid) || pdf.category === 'qbank-pdf') return null;
    if (/^mastery-/.test(lid) || /how to|must review|ultimate review/i.test(pdf.title || '')) return null;
    const i = CURRENT_BOOKS.findIndex(b => b.ids.includes(lid) || b.re.test(pdf.title || ''));
    return i < 0 ? null : { ...CURRENT_BOOKS[i], order: i };
  }
  // Picking a lecture suggests its book when none is chosen yet: Pink, Gold, Mark Klimek (Yellow Book), ICU Survival
  function suggestBookFor(video) {
    if (selectedPdfId || !video || !window.YNS_PDF_DATA) return;
    const want = video.category === 'crash-course' && /mastery/i.test(video.title || '') ? 'mastery' : ({ 'pink-book': 'pink', 'gold-book': 'gold', icu: 'icu', 'crash-course': 'crash' }[video.category] || 'yellow');
    const hit = window.YNS_PDF_DATA.getAllPdfs().find(p => { const b = bookFor(p); return b && b.key === want && (!window.YNSPdfLibrary || window.YNSPdfLibrary.hasPdfAccess(p.id)); });
    if (hit) { selectedPdfId = hit.id; updateSelectedPdf(); populatePdfSelector(); }
  }

  function freeGuide(id) {
    if (typeof id !== 'string' || id.indexOf('free:') !== 0) return null;
    return (window.YNSFreeGuides || []).filter(g => g.key === id.slice(5))[0] || null;
  }
  function getPdf(id) {
    const g = freeGuide(id);
    if (g) return { id: id, title: g.name, fileUrl: g.url, free: true };
    const d = window.YNS_PDF_DATA;
    return d ? d.getPdfById(id) : null;
  }

  function populatePdfSelector() {
    const menu = document.getElementById('pdfSelectorMenu');
    if (!menu) return;

    const pdfData = window.YNS_PDF_DATA;
    if (!pdfData) {
      menu.innerHTML = '<p class="yns-selector-loading">Loading PDFs...</p>';
      return;
    }

    const config = window.YNS_CONFIG || {};
    const canOpen = pdf => window.YNSPdfLibrary ? window.YNSPdfLibrary.hasPdfAccess(pdf.id) :
      (config.access?.pdfBundle || config.access?.pdfFundamentals || config.access?.pdfPharm);
    // Only books sold on the store today, in shelf order, with their short names; only the ones this customer owns
    const pdfs = pdfData.getAllPdfs()
      .map(pdf => ({ pdf, book: bookFor(pdf) }))
      .filter(x => x.book && canOpen(x.pdf))
      .sort((a, b) => a.book.order - b.book.order);

    let html = '';
    pdfs.forEach(({ pdf, book }) => {
      html += `<button type="button" class="yns-selector-option${pdf.id === selectedPdfId ? ' is-on' : ''}" data-pdf-id="${pdf.id}">${escapeHtml(book.name)}</button>`;
    });
    if (html) html = '<p class="yns-selector-group-label">Your books</p>' + html;
    // free guides: open for everyone who is logged in
    const loggedIn = !document.getElementById('ynsEducationHub')?.classList.contains('is-guest');
    const free = loggedIn ? (window.YNSFreeGuides || []) : [];
    if (free.length) {
      html += '<p class="yns-selector-group-label">Free guides</p>';
      free.forEach(g => { const id = 'free:' + g.key; html += `<button type="button" class="yns-selector-option${id === selectedPdfId ? ' is-on' : ''}" data-pdf-id="${id}"><span class="yns-selector-free">Free</span>${escapeHtml(g.name)}</button>`; });
    }
    // books still to unlock: greyed with a lock, tap for a peek and one-click unlock
    const locked = window.YNSLockedBooks ? window.YNSLockedBooks() : [];
    if (locked.length) {
      html += '<p class="yns-selector-group-label">Unlock more books</p>';
      locked.forEach(b => {
        const handle = (String(b.url || '').split('/products/')[1] || '').split(/[?#]/)[0];
        html += `<button type="button" class="yns-selector-option is-locked" data-ypeek="${escapeHtml(handle)}" data-ypeek-title="${escapeHtml(b.title)}" data-ypeek-kind="book"><span class="yns-selector-lock" aria-hidden="true">&#128274;</span>${escapeHtml(b.title)}</button>`;
      });
    }
    if (!html) html = '<div class="yns-selector-locked"><p>No books yet</p><a href="#books" data-yhub-tab="books">See My books</a></div>';

    menu.innerHTML = html;

    // Add click handlers
    menu.querySelectorAll('.yns-selector-option:not(.disabled):not(.is-locked)').forEach(option => {
      option.addEventListener('click', () => {
        selectedPdfId = option.dataset.pdfId;
        updateSelectedPdf();
        closeDropdown('pdfSelectorMenu');
        checkStartEnabled();
      });
    });
  }

  function updateSelectedVideo() {
    const textEl = document.getElementById('selectedVideoText');
    if (!textEl) return;

    const video = VIDEOS.find(v => v.id === selectedVideoId);
    textEl.textContent = video ? video.title : 'Choose a video...';
  }

  function updateSelectedPdf() {
    const textEl = document.getElementById('selectedPdfText');
    if (!textEl) return;

    const pdfData = window.YNS_PDF_DATA;
    const pdf = getPdf(selectedPdfId);
    const book = pdf && !pdf.free ? bookFor(pdf) : null;
    textEl.textContent = book ? book.name : (pdf ? pdf.title : 'Choose a book');
  }

  function checkStartEnabled() {
    const startBtn = document.getElementById('startStudyBtn');
    if (startBtn) {
      startBtn.disabled = !selectedVideoId || !selectedPdfId;
    }
  }

  // ============================================
  // Study Session
  // ============================================
  function startStudySession(videoTime = 0, pdfPage = 1) {
    if (!selectedVideoId || !selectedPdfId) return;
    returnTo = null;

    const video = VIDEOS.find(v => v.id === selectedVideoId);
    const pdfData = window.YNS_PDF_DATA;
    const pdf = getPdf(selectedPdfId);

    if (!video || !pdf) return;

    // Hide selectors, show split view
    document.querySelector('.yns-study-selectors').style.display = 'none';
    document.getElementById('sessionResumeCard').style.display = 'none';

    const splitView = document.getElementById('splitViewContainer');
    const notesSection = document.getElementById('studyNotesSection');

    splitView.style.display = 'flex';
    notesSection.style.display = 'block';

    // Update titles
    document.getElementById('studyVideoTitle').textContent = video.title;
    document.getElementById('studyPdfTitle').textContent = pdf.title;

    // Load video
    loadStudyVideo(video.vimeoId, videoTime);

    // Load PDF - fetch signed URL from Edge Function
    loadStudyPdfAsync(pdfPage);

    // Load annotations from Supabase in background
    loadStudyAnnotationsFromSupabase(selectedPdfId);

    // Show mobile toggle if needed
    if (isMobileView) {
      document.getElementById('mobileViewToggle').style.display = 'flex';
      setMobilePane('video');
    }

    // Render notes
    renderNotes();

    // Log activity
    document.dispatchEvent(new CustomEvent('yns:studySession', {
      detail: { videoId: selectedVideoId, pdfId: selectedPdfId },
      bubbles: true
    }));
  }

  function endStudySession() {
    // Save current state
    saveSession();

    // Cleanup
    if (vimeoPlayer) {
      vimeoPlayer.destroy();
      vimeoPlayer = null;
    }

    pdfDoc = null;
    currentPdfPage = 1;

    // Reset continuous scroll state
    renderedPages.clear();
    renderingPages.clear();
    if (scrollDebounceTimer) {
      clearTimeout(scrollDebounceTimer);
      scrollDebounceTimer = null;
    }

    // Reset annotation tool state
    currentTool = null;
    isDrawingHighlight = false;
    highlightPoints = [];
    activeDrawingLayer = null;
    document.querySelectorAll('.yns-study-tool').forEach(t => t.classList.remove('active'));
    document.getElementById('studyColorPicker')?.classList.remove('visible');
    document.getElementById('studyPdfScrollContainer')?.classList.remove('tool-active');

    // Reset UI
    document.querySelector('.yns-study-selectors').style.display = 'flex';
    document.getElementById('splitViewContainer').style.display = 'none';
    document.getElementById('studyNotesSection').style.display = 'none';
    document.getElementById('mobileViewToggle').style.display = 'none';

    // Clear video
    document.getElementById('studyVideoWrapper').innerHTML = '';

    // Clear PDF scroll container
    const scrollContainer = document.getElementById('studyPdfScrollContainer');
    if (scrollContainer) {
      scrollContainer.innerHTML = '';
    }

    checkForResumableSession();
  }

  // ============================================
  // Video Loading
  // ============================================
  // Private-link code (h=...) for unlisted Vimeo videos such as the Gold Book lectures
  function studyVimeoHashParam(vimeoId) {
    const meta = (VIDEOS || []).find(v => String(v.vimeoId) === String(vimeoId));
    return meta && meta.vimeoHash ? `h=${meta.vimeoHash}&` : '';
  }

  function loadStudyVideo(vimeoId, startTime = 0) {
    const wrapper = document.getElementById('studyVideoWrapper');
    if (!wrapper) return;

    if (String(vimeoId).indexOf('yt:') === 0) {
      vimeoPlayer = null;
      wrapper.innerHTML = `<iframe id="studyVimeoPlayer" src="https://www.youtube-nocookie.com/embed/${String(vimeoId).slice(3)}?rel=0&modestbranding=1${startTime > 0 ? '&start=' + Math.floor(startTime) : ''}" title="Lecture" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
      return;
    }

    // Create iframe
    wrapper.innerHTML = `
      <iframe
        id="studyVimeoPlayer"
        src="https://player.vimeo.com/video/${vimeoId}?${studyVimeoHashParam(vimeoId)}autoplay=0&title=0&byline=0&portrait=0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowfullscreen>
      </iframe>
    `;

    // Initialize Vimeo player
    setTimeout(() => {
      const iframe = document.getElementById('studyVimeoPlayer');
      if (iframe && typeof Vimeo !== 'undefined') {
        vimeoPlayer = new Vimeo.Player(iframe);

        // Set start time
        if (startTime > 0) {
          vimeoPlayer.setCurrentTime(startTime);
        }

        // Track progress
        vimeoPlayer.on('timeupdate', (data) => {
          if (currentSession) {
            currentSession.videoTime = Math.floor(data.seconds);
          }
        });

        // Auto-save on pause
        vimeoPlayer.on('pause', () => {
          saveSession();
        });
      }
    }, 500);
  }

  // ============================================
  // PDF Loading
  // ============================================

  /**
   * Fetch signed URL from Edge Function and load the PDF.
   * Uses YNSPdfLibrary.getPdfUrl() for consistent URL fetching.
   */
  async function loadStudyPdfAsync(startPage = 1) {
    const container = document.getElementById('studyPdfScrollContainer');
    if (!container || !selectedPdfId) return;

    // Show loading state
    container.innerHTML = '<div class="yns-pdf-page-loading">Loading PDF...</div>';

    try {
      // Use the PDF library's async URL fetching (which handles caching and Edge Function)
      let fileUrl;
      if (freeGuide(selectedPdfId)) {
        fileUrl = freeGuide(selectedPdfId).url;
      } else if (window.YNSPdfLibrary && window.YNSPdfLibrary.getPdfUrl) {
        fileUrl = await window.YNSPdfLibrary.getPdfUrl(selectedPdfId);
      } else {
        // Fallback to legacy fileUrl if PDF library not available
        const pdfData = window.YNS_PDF_DATA;
        const pdf = getPdf(selectedPdfId);
        fileUrl = pdf ? pdf.fileUrl : null;
      }

      if (!fileUrl) {
        throw new Error('Could not get PDF URL');
      }

      await loadStudyPdf(fileUrl, startPage);

    } catch (error) {
      console.error('Error loading study PDF:', error);
      container.innerHTML = `
        <div class="yns-pdf-load-error">
          <p>Failed to load PDF</p>
          <p class="yns-error-detail">${error.message || 'Please try again'}</p>
          <button class="yns-btn-retry" onclick="window.YNSStudyMode.retryPdfLoad && window.YNSStudyMode.retryPdfLoad()">
            Retry
          </button>
        </div>
      `;
    }
  }

  async function loadStudyPdf(fileUrl, startPage = 1) {
    const container = document.getElementById('studyPdfScrollContainer');
    if (!container) return;

    try {
      const loadingTask = pdfjsLib.getDocument(fileUrl);
      pdfDoc = await loadingTask.promise;

      totalPdfPages = pdfDoc.numPages;
      currentPdfPage = Math.min(startPage, totalPdfPages);

      updatePdfPageInfo();
      await initContinuousScroll();

      // Scroll to start page if resuming session
      if (startPage > 1) {
        setTimeout(() => goToPdfPage(startPage), 100);
      }

    } catch (error) {
      console.error('Error loading study PDF:', error);
    }
  }

  // ---------- Zoom + sharp rendering ----------
  let pdfZoom = (() => { try { const z = parseFloat(localStorage.getItem('yns_pdf_zoom')); return z >= 0.5 && z <= 3 ? z : 1; } catch (e) { return 1; } })();
  function pageScale(viewport) {
    const wrapper = document.getElementById('studyPdfWrapper');
    const fit = Math.max(200, (wrapper ? wrapper.clientWidth : 800) - 40) / viewport.width;
    return fit * pdfZoom;
  }
  // Canvas pixels per CSS pixel: screen density, plus extra detail when zoomed in (capped to keep memory sane)
  function outputScale(cssW, cssH) {
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const want = Math.min(dpr * 1.25, 3);
    const maxPixels = 9e6; // about 3000x3000 per page
    return Math.min(want, Math.sqrt(maxPixels / Math.max(1, cssW * cssH)));
  }
  function updateZoomLabel() {
    const l = document.getElementById('studyPdfZoomLabel');
    if (l) l.textContent = Math.round(pdfZoom * 100) + '%';
  }
  let zoomTimer = null;
  function setPdfZoom(z) {
    const next = Math.min(3, Math.max(0.5, Math.round(z * 10) / 10));
    if (next === pdfZoom || !pdfDoc) { pdfZoom = next; updateZoomLabel(); return; }
    pdfZoom = next;
    try { localStorage.setItem('yns_pdf_zoom', String(pdfZoom)); } catch (e) {}
    updateZoomLabel();
    clearTimeout(zoomTimer);
    zoomTimer = setTimeout(async () => {
      const keepPage = currentPdfPage;
      const wrapper = document.getElementById('studyPdfWrapper');
      const before = wrapper ? { y: (wrapper.scrollTop + wrapper.clientHeight / 2) / Math.max(1, wrapper.scrollHeight), x: (wrapper.scrollLeft + wrapper.clientWidth / 2) / Math.max(1, wrapper.scrollWidth) } : null;
      renderedPages.clear();
      await initContinuousScroll();
      if (wrapper && before) {
        wrapper.scrollTop = before.y * wrapper.scrollHeight - wrapper.clientHeight / 2;
        wrapper.scrollLeft = before.x * wrapper.scrollWidth - wrapper.clientWidth / 2;
      }
      currentPdfPage = keepPage; updatePageInput();
      await renderVisiblePages();
    }, 120);
  }

  async function initContinuousScroll() {
    const container = document.getElementById('studyPdfScrollContainer');
    container.innerHTML = '';
    renderedPages.clear();

    // Get first page to calculate dimensions for placeholders
    const firstPage = await pdfDoc.getPage(1);
    const wrapper = document.getElementById('studyPdfWrapper');
    const wrapperWidth = wrapper.clientWidth - 40;
    const viewport = firstPage.getViewport({ scale: 1 });
    const scale = pageScale(viewport);
    const scaledViewport = firstPage.getViewport({ scale });

    const pageWidth = scaledViewport.width;
    const pageHeight = scaledViewport.height;

    // Create placeholder wrappers for all pages
    for (let pageNum = 1; pageNum <= totalPdfPages; pageNum++) {
      const pageWrapper = document.createElement('div');
      pageWrapper.className = 'yns-pdf-page-wrapper';
      pageWrapper.dataset.page = pageNum;
      pageWrapper.style.width = pageWidth + 'px';
      pageWrapper.style.height = pageHeight + 'px';

      // Loading indicator
      const loading = document.createElement('div');
      loading.className = 'yns-pdf-page-loading';
      loading.textContent = `Page ${pageNum}`;
      pageWrapper.appendChild(loading);

      container.appendChild(pageWrapper);
    }

    // Render visible pages initially
    await renderVisiblePages();
  }

  async function renderVisiblePages() {
    const wrapper = document.getElementById('studyPdfWrapper');
    const wrapperHeight = wrapper.clientHeight;
    const wrapperRect = wrapper.getBoundingClientRect();

    const pageWrappers = document.querySelectorAll('.yns-pdf-page-wrapper');
    const pagesToRender = [];

    // First pass: identify which pages need rendering
    for (const pageWrapper of pageWrappers) {
      const pageNum = parseInt(pageWrapper.dataset.page, 10);

      // Skip already rendered or currently rendering pages
      if (renderedPages.has(pageNum) || renderingPages.has(pageNum)) continue;

      const rect = pageWrapper.getBoundingClientRect();

      // Check if page is within viewport + buffer
      const isNearViewport =
        rect.bottom > wrapperRect.top - (wrapperHeight * RENDER_BUFFER) &&
        rect.top < wrapperRect.bottom + (wrapperHeight * RENDER_BUFFER);

      if (isNearViewport) {
        pagesToRender.push({ pageNum, pageWrapper });
      }
    }

    // Render all visible pages in parallel
    if (pagesToRender.length > 0) {
      await Promise.all(
        pagesToRender.map(({ pageNum, pageWrapper }) =>
          renderSinglePage(pageNum, pageWrapper)
        )
      );
    }
  }

  async function renderSinglePage(pageNum, pageWrapper) {
    // Skip if already rendered or currently rendering
    if (renderedPages.has(pageNum) || renderingPages.has(pageNum)) return;

    // Mark as rendering to prevent duplicate render attempts
    renderingPages.add(pageNum);

    try {
      const page = await pdfDoc.getPage(pageNum);
      const wrapper = document.getElementById('studyPdfWrapper');
      const wrapperWidth = wrapper.clientWidth - 40;
      const viewport = page.getViewport({ scale: 1 });
      const scale = pageScale(viewport);
      const scaledViewport = page.getViewport({ scale });
      const ratio = outputScale(scaledViewport.width, scaledViewport.height);

      // Clear loading placeholder
      pageWrapper.innerHTML = '';

      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.className = 'yns-pdf-page-canvas';
      // Draw at screen density so text is crisp; CSS keeps it at the page size
      canvas.width = Math.floor(scaledViewport.width * ratio);
      canvas.height = Math.floor(scaledViewport.height * ratio);
      canvas.style.width = Math.floor(scaledViewport.width) + 'px';
      canvas.style.height = Math.floor(scaledViewport.height) + 'px';

      // Create annotation layer
      const annotationLayer = document.createElement('div');
      annotationLayer.className = 'yns-study-annotation-layer';
      annotationLayer.dataset.page = pageNum;
      // Dimensions inherited from CSS: width: 100%; height: 100%

      // If a tool is currently active, add tool-active to this new layer
      if (currentTool === 'highlight' || currentTool === 'note') {
        annotationLayer.classList.add('tool-active');
      }

      // Set wrapper dimensions FIRST (before appending children)
      // This ensures annotation layer's 100% width/height CSS resolves correctly
      pageWrapper.style.width = scaledViewport.width + 'px';
      pageWrapper.style.height = scaledViewport.height + 'px';

      pageWrapper.appendChild(canvas);
      pageWrapper.appendChild(annotationLayer);

      // Render PDF page
      await page.render({
        canvasContext: canvas.getContext('2d'),
        viewport: scaledViewport,
        transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : null
      }).promise;

      // Render annotations for this page
      renderPageAnnotations(pageNum, annotationLayer);

      // Setup annotation layer event handlers for this page
      setupAnnotationLayerEvents(annotationLayer);

      renderedPages.add(pageNum);
    } finally {
      // Always remove from rendering set
      renderingPages.delete(pageNum);
    }
  }

  function handlePdfScroll(e) {
    // Immediate: render pages that are visible (no debounce for faster loading)
    renderVisiblePages();

    // Debounced: update current page indicator only
    if (scrollDebounceTimer) clearTimeout(scrollDebounceTimer);

    scrollDebounceTimer = setTimeout(() => {
      const wrapper = e.target;
      const wrapperRect = wrapper.getBoundingClientRect();

      // Find which page is most visible (centered in viewport)
      const pageWrappers = document.querySelectorAll('.yns-pdf-page-wrapper');
      let currentVisiblePage = 1;
      let minDistance = Infinity;

      pageWrappers.forEach(pw => {
        const rect = pw.getBoundingClientRect();
        const pageCenter = rect.top + rect.height / 2;
        const viewportCenter = wrapperRect.top + wrapperRect.height / 2;
        const distance = Math.abs(pageCenter - viewportCenter);

        if (distance < minDistance) {
          minDistance = distance;
          currentVisiblePage = parseInt(pw.dataset.page, 10);
        }
      });

      if (currentPdfPage !== currentVisiblePage) {
        currentPdfPage = currentVisiblePage;
        updatePageInput();
        updateBookmarkButton();
      }
    }, 100);  // Increased debounce for page indicator only (UI updates don't need to be immediate)
  }

  function goToPdfPage(pageNum) {
    if (pageNum < 1 || pageNum > totalPdfPages) return;

    const pageWrapper = document.querySelector(`.yns-pdf-page-wrapper[data-page="${pageNum}"]`);
    if (pageWrapper) {
      pageWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      currentPdfPage = pageNum;
      updatePageInput();
    }
  }

  function updatePdfPageInfo() {
    // Update page input and total (new UI)
    updatePageInput();

    const prevBtn = document.getElementById('studyPdfPrev');
    const nextBtn = document.getElementById('studyPdfNext');

    if (prevBtn) prevBtn.disabled = currentPdfPage <= 1;
    if (nextBtn) nextBtn.disabled = currentPdfPage >= totalPdfPages;
  }

  // ============================================
  // Split View Resizing
  // ============================================
  function initResizer() {
    const resizer = document.getElementById('splitResizer');
    const container = document.getElementById('splitViewContainer');
    const videoPane = document.getElementById('videoPane');
    const pdfPane = document.getElementById('pdfPane');

    if (!resizer || !container) return;

    resizer.addEventListener('mousedown', (e) => {
      isResizing = true;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    });

    document.addEventListener('mousemove', (e) => {
      if (!isResizing) return;

      const containerRect = container.getBoundingClientRect();
      const newRatio = (e.clientX - containerRect.left) / containerRect.width;

      // Enforce minimum widths
      const minRatio = CONFIG.minPaneWidth / containerRect.width;
      const maxRatio = 1 - minRatio;

      splitRatio = Math.max(minRatio, Math.min(maxRatio, newRatio));

      videoPane.style.flex = `0 0 ${splitRatio * 100}%`;
      pdfPane.style.flex = `0 0 ${(1 - splitRatio) * 100}%`;
    });

    document.addEventListener('mouseup', () => {
      if (isResizing) {
        isResizing = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    });
  }

  // ============================================
  // In-Session Content Switching
  // ============================================
  function switchVideo(videoId) {
    if (videoId === selectedVideoId) return;

    // Save current video state
    saveSession();

    const video = VIDEOS.find(v => v.id === videoId);
    if (!video) return;

    selectedVideoId = videoId;

    // Update title
    document.getElementById('studyVideoTitle').textContent = video.title;

    // Load new video starting at 0:00
    loadStudyVideo(video.vimeoId, 0);

    // Update session
    if (currentSession) {
      currentSession.videoId = videoId;
      currentSession.videoTime = 0;
    }

    // Close dropdown
    closePaneMenu('videoPaneMenu');

    // Re-populate menu to show checkmark on new selection
    populateVideoPaneMenu();

    showToast('Video Switched', `Now playing: ${video.title}`);
  }

  function switchPdf(pdfId) {
    if (pdfId === selectedPdfId) return;

    // Save current PDF state
    saveSession();

    const pdfData = window.YNS_PDF_DATA;
    const pdf = getPdf(pdfId);
    if (!pdf) return;

    selectedPdfId = pdfId;

    // Update title
    document.getElementById('studyPdfTitle').textContent = pdf.title;

    // Load new PDF starting at page 1 - fetch signed URL from Edge Function
    loadStudyPdfAsync(1);

    // Update session
    if (currentSession) {
      currentSession.pdfId = pdfId;
      currentSession.pdfPage = 1;
    }

    // Close dropdown
    closePaneMenu('pdfPaneMenu');

    // Re-populate menu to show checkmark on new selection
    populatePdfPaneMenu();

    showToast('PDF Switched', `Now viewing: ${pdf.title}`);
  }

  function populateVideoPaneMenu() {
    const menu = document.getElementById('videoPaneMenu');
    if (!menu) return;

    const config = window.YNS_CONFIG || {};
    const hasAccess = config.access?.videos || false;

    if (!hasAccess) {
      menu.innerHTML = `
        <div class="yns-inpane-menu-locked">
          <span>🔒</span>
          <span>Video access required</span>
        </div>
      `;
      return;
    }

    // Group by category
    const byCategory = {};
    VIDEOS.forEach(video => {
      if (!byCategory[video.category]) {
        byCategory[video.category] = [];
      }
      byCategory[video.category].push(video);
    });

    let html = '';
    Object.entries(byCategory).forEach(([category, videos]) => {
      html += `<div class="yns-inpane-menu-group">${formatCategory(category)}</div>`;
      videos.forEach(video => {
        const isSelected = video.id === selectedVideoId;
        html += `
          <button class="yns-inpane-menu-item ${isSelected ? 'selected' : ''}" data-video-id="${video.id}">
            ${isSelected ? '<span class="yns-inpane-check">✓</span>' : ''}
            <span class="yns-inpane-item-title">${video.title}</span>
          </button>
        `;
      });
    });

    menu.innerHTML = html;

    // Add click handlers
    menu.querySelectorAll('.yns-inpane-menu-item').forEach(item => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        const videoId = parseInt(item.dataset.videoId);
        switchVideo(videoId);
      });
    });
  }

  function populatePdfPaneMenu() {
    const menu = document.getElementById('pdfPaneMenu');
    if (!menu) return;

    const pdfData = window.YNS_PDF_DATA;
    if (!pdfData) {
      menu.innerHTML = '<div class="yns-inpane-menu-loading">Loading PDFs...</div>';
      return;
    }

    const pdfs = pdfData.getAllPdfs();
    const config = window.YNS_CONFIG || {};

    let html = '';
    pdfs.forEach(pdf => {
      const hasAccess = window.YNSPdfLibrary ?
        window.YNSPdfLibrary.hasPdfAccess(pdf.id) :
        (config.access?.pdfBundle || config.access?.pdfFundamentals || config.access?.pdfPharm);

      const isSelected = pdf.id === selectedPdfId;

      html += `
        <button class="yns-inpane-menu-item ${isSelected ? 'selected' : ''} ${hasAccess ? '' : 'locked'}"
                data-pdf-id="${pdf.id}"
                ${hasAccess ? '' : 'disabled'}>
          ${isSelected ? '<span class="yns-inpane-check">✓</span>' : ''}
          ${!hasAccess ? '<span class="yns-inpane-lock">🔒</span>' : ''}
          <span class="yns-inpane-item-title">${pdf.title}</span>
        </button>
      `;
    });

    menu.innerHTML = html;

    // Add click handlers
    menu.querySelectorAll('.yns-inpane-menu-item:not(.locked)').forEach(item => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        const pdfId = item.dataset.pdfId;
        switchPdf(pdfId);
      });
    });
  }

  function togglePaneMenu(menuId) {
    const menu = document.getElementById(menuId);
    if (!menu) return;

    const wasOpen = menu.classList.contains('active');
    closeAllPaneMenus();

    if (!wasOpen) {
      menu.classList.add('active');
      // Populate the menu when opening
      if (menuId === 'videoPaneMenu') {
        populateVideoPaneMenu();
      } else if (menuId === 'pdfPaneMenu') {
        populatePdfPaneMenu();
      }
    }
  }

  function closePaneMenu(menuId) {
    const menu = document.getElementById(menuId);
    if (menu) {
      menu.classList.remove('active');
    }
  }

  function closeAllPaneMenus() {
    document.querySelectorAll('.yns-inpane-menu').forEach(menu => {
      menu.classList.remove('active');
    });
  }

  // ============================================
  // Fullscreen Mode
  // ============================================
  function toggleFullscreen() {
    const targetElement = document.getElementById('splitViewContainer');
    if (!targetElement) return;

    // Check if we're currently in fullscreen
    const currentFullscreenElement = document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (currentFullscreenElement || isCssFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen(targetElement);
    }
  }

  function enterFullscreen(element) {
    fullscreenTarget = 'split';

    // Try native Fullscreen API first
    if (element.requestFullscreen) {
      element.requestFullscreen().catch(handleFullscreenError);
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen();
    } else {
      // iOS Safari fallback - use CSS fullscreen
      applyCssFullscreen(element);
    }
  }

  function exitFullscreen() {
    if (isCssFullscreen) {
      removeCssFullscreen();
      return;
    }

    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  }

  function applyCssFullscreen(element) {
    isCssFullscreen = true;
    isFullscreen = true;
    element.classList.add('yns-css-fullscreen');
    document.body.classList.add('yns-fullscreen-active');

    // Add escape key handler
    document.addEventListener('keydown', handleCssFullscreenEscape);

    // Update button states
    updateFullscreenButtons();

    // Re-render PDF at new scale
    if (fullscreenTarget === 'pdf' || fullscreenTarget === 'split') {
      setTimeout(async () => {
        if (pdfDoc) {
          await rerenderAllPages();
        }
      }, 100);
    }
  }

  function removeCssFullscreen() {
    isCssFullscreen = false;
    isFullscreen = false;

    // Remove CSS fullscreen class from all possible targets
    document.querySelectorAll('.yns-css-fullscreen').forEach(el => {
      el.classList.remove('yns-css-fullscreen');
    });
    document.body.classList.remove('yns-fullscreen-active');

    // Remove escape key handler
    document.removeEventListener('keydown', handleCssFullscreenEscape);

    fullscreenTarget = null;

    // Update button states
    updateFullscreenButtons();

    // Re-render PDF at original scale
    setTimeout(async () => {
      if (pdfDoc) {
        await rerenderAllPages();
      }
    }, 100);
  }

  function handleCssFullscreenEscape(e) {
    if (e.key === 'Escape' && isCssFullscreen) {
      removeCssFullscreen();
    }
  }

  function handleFullscreenError(err) {
    const element = document.getElementById('splitViewContainer');
    if (element) {
      applyCssFullscreen(element);
    }
  }

  function handleFullscreenChange() {
    const fullscreenElement = document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    isFullscreen = !!fullscreenElement;

    if (!isFullscreen) {
      fullscreenTarget = null;
    }

    // Update button states
    updateFullscreenButtons();

    // Re-render PDF at new scale when entering/exiting fullscreen
    setTimeout(async () => {
      if (pdfDoc) {
        await rerenderAllPages();
      }
    }, 100);
  }

  // Re-render all pages (used after resize/fullscreen changes)
  async function rerenderAllPages() {
    const currentScrollTop = document.getElementById('studyPdfWrapper')?.scrollTop || 0;

    // Clear rendered pages and re-init
    renderedPages.clear();
    await initContinuousScroll();

    // Restore scroll position
    const wrapper = document.getElementById('studyPdfWrapper');
    if (wrapper) {
      wrapper.scrollTop = currentScrollTop;
    }

    // Re-render visible pages
    await renderVisiblePages();
  }

  function updateFullscreenButtons() {
    const splitBtn = document.getElementById('splitViewFullscreenBtn');

    const exitIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
    </svg>`;

    const enterIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
    </svg>`;

    if (splitBtn) {
      splitBtn.innerHTML = (isFullscreen || isCssFullscreen) ? exitIcon : enterIcon;
    }
  }

  function getFullscreenState() {
    return {
      isFullscreen: isFullscreen || isCssFullscreen,
      target: fullscreenTarget,
      isCssFallback: isCssFullscreen
    };
  }

  // ============================================
  // Mobile View
  // ============================================
  function setMobilePane(pane) {
    currentMobilePane = pane;

    const videoPane = document.getElementById('videoPane');
    const pdfPane = document.getElementById('pdfPane');
    const toggleBtns = document.querySelectorAll('.yns-toggle-btn');

    if (pane === 'video') {
      videoPane.classList.remove('hidden');
      pdfPane.classList.add('hidden');
    } else {
      videoPane.classList.add('hidden');
      pdfPane.classList.remove('hidden');
    }

    toggleBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.view === pane);
    });
  }

  let resizeDebounceTimer = null;

  function handleResize() {
    const wasMobile = isMobileView;
    isMobileView = window.innerWidth <= 768;

    if (wasMobile !== isMobileView) {
      const splitView = document.getElementById('splitViewContainer');
      if (splitView && splitView.style.display !== 'none') {
        const mobileToggle = document.getElementById('mobileViewToggle');
        if (isMobileView) {
          mobileToggle.style.display = 'flex';
          setMobilePane('video');
        } else {
          mobileToggle.style.display = 'none';
          document.getElementById('videoPane').classList.remove('hidden');
          document.getElementById('pdfPane').classList.remove('hidden');
        }
      }
    }

    // Debounce PDF re-render on resize
    if (resizeDebounceTimer) clearTimeout(resizeDebounceTimer);
    resizeDebounceTimer = setTimeout(async () => {
      if (pdfDoc && document.getElementById('studyPdfScrollContainer')?.children.length > 0) {
        await rerenderAllPages();
      }
    }, 300);
  }

  // ============================================
  // Event Listeners
  // ============================================
  function setupEventListeners() {
    // Dropdown toggles
    document.getElementById('videoSelectorBtn')?.addEventListener('click', () => {
      populateVideoSelector();
      toggleDropdown('videoSelectorMenu');
    });

    document.getElementById('studyPdfZoomIn')?.addEventListener('click', () => setPdfZoom(pdfZoom + 0.2));
    document.getElementById('studyPdfZoomOut')?.addEventListener('click', () => setPdfZoom(pdfZoom - 0.2));
    document.getElementById('studyPdfZoomFit')?.addEventListener('click', () => setPdfZoom(1));
    (function enablePan() {
      const w = document.getElementById('studyPdfWrapper'); if (!w) return;
      let drag = null;
      w.addEventListener('pointerdown', (e) => {
        if (currentTool || e.button !== 0 || e.target.closest('textarea, input, button, .yns-study-note')) return;
        if (w.scrollWidth <= w.clientWidth + 2 && w.scrollHeight <= w.clientHeight + 2) return;
        drag = { x: e.clientX, y: e.clientY, l: w.scrollLeft, t: w.scrollTop, moved: false };
        w.classList.add('is-panning');
      });
      window.addEventListener('pointermove', (e) => {
        if (!drag) return;
        const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 3) drag.moved = true;
        w.scrollLeft = drag.l - dx; w.scrollTop = drag.t - dy;
      });
      window.addEventListener('pointerup', () => { if (drag) { drag = null; w.classList.remove('is-panning'); } });
      w.addEventListener('keydown', (e) => {
        if (e.target.closest('textarea, input')) return;
        if (e.key === '+' || e.key === '=') { e.preventDefault(); setPdfZoom(pdfZoom + 0.2); }
        if (e.key === '-') { e.preventDefault(); setPdfZoom(pdfZoom - 0.2); }
        if (e.key === '0') { e.preventDefault(); setPdfZoom(1); }
      });
      w.tabIndex = 0;
    })();
    document.getElementById('studyPdfWrapper')?.addEventListener('wheel', (e) => {
      if (!e.ctrlKey && !e.metaKey) return; // pinch on a trackpad arrives as ctrl+wheel
      e.preventDefault();
      setPdfZoom(pdfZoom * (e.deltaY < 0 ? 1.1 : 1 / 1.1));
    }, { passive: false });
    updateZoomLabel();
    document.getElementById('pdfSelectorBtn')?.addEventListener('click', () => {
      populatePdfSelector();
      toggleDropdown('pdfSelectorMenu');
    });

    // Start button
    document.getElementById('startStudyBtn')?.addEventListener('click', () => {
      startStudySession();
    });

    // Resume button
    document.getElementById('resumeSessionBtn')?.addEventListener('click', () => {
      resumeSession();
    });

    // PDF navigation
    document.getElementById('studyPdfPrev')?.addEventListener('click', () => {
      goToPdfPage(currentPdfPage - 1);
    });

    document.getElementById('studyPdfNext')?.addEventListener('click', () => {
      goToPdfPage(currentPdfPage + 1);
    });

    // Page input for jump-to-page
    document.getElementById('studyPdfPageInput')?.addEventListener('keydown', handlePageInput);
    document.getElementById('studyPdfPageInput')?.addEventListener('blur', (e) => {
      // Validate and apply on blur
      const pageNum = parseInt(e.target.value, 10);
      if (pageNum >= 1 && pageNum <= totalPdfPages && pageNum !== currentPdfPage) {
        goToPdfPage(pageNum);
      } else {
        e.target.value = currentPdfPage;
      }
    });

    // Annotation tools
    document.querySelectorAll('.yns-study-tool').forEach(btn => {
      btn.addEventListener('click', () => {
        setStudyTool(btn.dataset.tool);
      });
    });

    // Color picker
    document.querySelectorAll('.yns-study-color-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        setStudyColor(btn.dataset.color);
      });
    });

    // Set default color as active
    setStudyColor(currentHighlightColor);

    // Scroll handler for continuous scroll PDF (annotation layers are set up per-page in renderSinglePage)
    document.getElementById('studyPdfWrapper')?.addEventListener('scroll', handlePdfScroll);

    // Listen for annotation updates from PDF viewer
    document.addEventListener('yns:annotationsUpdated', (e) => {
      if (e.detail.pdfId === selectedPdfId) {
        loadStudyAnnotations();
        refreshAnnotations();
      }
    });

    // Add note button
    // "x Close" ends the session and returns to the Study mode front page
    document.getElementById('studyExitBtn')?.addEventListener('click', () => {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
      endStudySession();
      checkForResumableSession();
      if (returnTo) { const go = returnTo; returnTo = null; go(); return; }
      document.getElementById('panel-study')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // Notes are typed straight into the box under the viewer (Enter or "Save note" saves; Shift+Enter adds a line)
    const noteInput = document.getElementById('noteInput');
    const noteSave = document.getElementById('addNoteBtn');
    const saveTypedNote = () => {
      const content = noteInput ? noteInput.value.trim() : '';
      if (!content) return;
      addNote(content);
      noteInput.value = '';
      if (noteSave) noteSave.disabled = true;
      noteInput.focus();
    };
    noteSave?.addEventListener('click', saveTypedNote);
    noteInput?.addEventListener('input', () => { if (noteSave) noteSave.disabled = !noteInput.value.trim(); });
    noteInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) { e.preventDefault(); saveTypedNote(); }
    });

    // Mobile view toggle
    document.querySelectorAll('.yns-toggle-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        setMobilePane(btn.dataset.view);
      });
    });

    // In-pane title buttons for content switching
    document.getElementById('videoTitleBtn')?.addEventListener('click', (e) => {
      e.stopPropagation();
      togglePaneMenu('videoPaneMenu');
    });

    document.getElementById('pdfTitleBtn')?.addEventListener('click', (e) => {
      e.stopPropagation();
      togglePaneMenu('pdfPaneMenu');
    });

    // Fullscreen button
    document.getElementById('splitViewFullscreenBtn')?.addEventListener('click', () => {
      toggleFullscreen();
    });

    // Fullscreen change events (for native fullscreen)
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Close dropdowns on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.yns-selector-dropdown')) {
        closeAllDropdowns();
      }
      // Also close pane menus
      if (!e.target.closest('.yns-pane-title-wrapper')) {
        closeAllPaneMenus();
      }
    });

    // Initialize resizer
    initResizer();

    // Save session periodically
    setInterval(() => {
      if (selectedVideoId && selectedPdfId) {
        saveSession();
      }
    }, 30000); // Every 30 seconds

    // Save on tab change
    document.addEventListener('yns:tabchange', (e) => {
      if (e.detail.previousTab === 'study') {
        saveSession();
      }
    });

    // Save on page unload
    window.addEventListener('beforeunload', () => {
      saveSession();
    });
  }

  function toggleDropdown(menuId) {
    const menu = document.getElementById(menuId);
    if (!menu) return;

    const isOpen = menu.classList.contains('active');
    closeAllDropdowns();

    if (!isOpen) {
      menu.classList.add('active');
    }
  }

  function closeDropdown(menuId) {
    const menu = document.getElementById(menuId);
    if (menu) {
      menu.classList.remove('active');
    }
  }

  function closeAllDropdowns() {
    document.querySelectorAll('.yns-selector-menu').forEach(menu => {
      menu.classList.remove('active');
    });
  }

  // ============================================
  // Utility Functions
  // ============================================
  function generateId() {
    return 'note_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  function formatCategory(category) {
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function showToast(title, message) {
    const toast = document.getElementById('toast');
    const toastTitle = document.getElementById('toastTitle');
    const toastMessage = document.getElementById('toastMessage');

    if (toast && toastTitle && toastMessage) {
      toastTitle.textContent = title;
      toastMessage.textContent = message;
      toast.classList.add('show');
      setTimeout(() => toast.classList.remove('show'), 3000);
    }
  }

  // ============================================
  // Public API
  // ============================================
  // Open Study mode straight into a lecture + its book (used by "Study side by side" on lecture pages)
  function startWith(videoId, pdfId) {
    if (!VIDEOS.length) loadVideosFromDOM();
    const v = VIDEOS.find(x => String(x.id) === String(videoId));
    if (!v || !pdfId) return false;
    if (currentSession) { try { endStudySession(); } catch (e) {} }
    selectedVideoId = v.id; selectedPdfId = pdfId;
    updateSelectedVideo(); updateSelectedPdf(); checkStartEnabled();
    const back = window.YNSStudyReturn || null; window.YNSStudyReturn = null;
    startStudySession(0, 1);
    returnTo = back;
    return true;
  }

  window.YNSStudyMode = {
    startWith,
    start: startStudySession,
    end: endStudySession,
    resume: resumeSession,
    saveSession,
    addNote,
    goToPdfPage,
    setMobilePane,
    getCurrentSession: () => currentSession,
    // Content switching
    switchVideo,
    switchPdf,
    // Fullscreen
    toggleFullscreen,
    isFullscreen: () => isFullscreen || isCssFullscreen,
    getFullscreenTarget: () => fullscreenTarget,
    getFullscreenState,
    // Annotations
    setStudyTool,
    setStudyColor,
    toggleStudyBookmark,
    addStudyAnnotation,
    removeStudyAnnotation,
    getAnnotationsForCurrentPdf,
    // PDF retry
    retryPdfLoad: () => loadStudyPdfAsync(currentPdfPage)
  };

  // ============================================
  // Initialize
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

// Add study mode specific styles
const studyStyles = document.createElement('style');
studyStyles.textContent = `
  .yns-selector-locked {
    padding: 20px;
    text-align: center;
    color: var(--yns-text-muted);
  }

  .yns-selector-locked a {
    display: inline-block;
    margin-top: 8px;
    color: var(--yns-sage);
    text-decoration: underline;
  }

  .yns-selector-loading {
    padding: 20px;
    text-align: center;
    color: var(--yns-text-muted);
  }

  .yns-selector-group-label {
    padding: 8px 16px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--yns-text-muted);
    background: var(--yns-cream);
    position: sticky;
    top: 0;
  }

  #studyVimeoPlayer {
    width: 100%;
    height: 100%;
    border: none;
  }

  #studyPdfCanvas {
    display: block;
    box-shadow: var(--yns-shadow-md);
    background: white;
  }

  .yns-no-notes {
    text-align: center;
    color: var(--yns-text-muted);
    padding: 20px;
    font-size: 0.875rem;
  }

  .yns-note-delete {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    color: var(--yns-text-muted);
    font-size: 1.25rem;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .yns-note-item {
    position: relative;
    cursor: pointer;
  }

  .yns-note-item:hover .yns-note-delete {
    opacity: 1;
  }

  .yns-note-item:hover .yns-note-delete:hover {
    color: var(--yns-error);
  }

  @media (max-width: 768px) {
    .yns-split-pane.hidden {
      display: none !important;
    }
  }

  /* PDF load error state */
  .yns-pdf-load-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 40px 20px;
    text-align: center;
    color: var(--yns-text-secondary, #666);
  }

  .yns-pdf-load-error p {
    margin: 0;
  }

  .yns-pdf-load-error .yns-error-detail {
    font-size: 0.875rem;
    color: var(--yns-text-muted, #999);
  }

  .yns-btn-retry {
    margin-top: 8px;
    padding: 8px 20px;
    background: var(--yns-sage, #46523E);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .yns-btn-retry:hover {
    background: var(--yns-sage-dark, #3a4534);
  }
`;
document.head.appendChild(studyStyles);

